import type { V1User } from '../client'

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

// 模型评测独立站点鉴权架构说明：
//   - 新站点使用飞书 OAuth 登录，Session 是 Worker 签发的 __Host-token Cookie
//   - backend-ng 评测接口的 Bearer token 由 Worker 代理层注入，前端**不持有、不需要** storageToken
//   - 为了不改动从 admin-ng copy 过来的大量代码，这里保留 useUserStore 的接口（user、storageToken、login、me、logout），但：
//       * storageToken 永远为空字符串（满足 useApi 拦截器里的 falsy 判断，自然不会注入 Authorization）
//       * user.id 映射到飞书 openId，user.username 映射到飞书 name，user.avatar 映射到 avatarUrl
//       * login 不再使用；调用它会跳转到飞书授权
//       * logout 调用 Worker 的 /api/auth/logout 并跳回登录
//       * me() 调用 Worker 的 /api/auth/me（飞书身份）

export interface User extends V1User {
  avatar?: string
}

export interface LoginCredentials {
  username: string
  password: string
  rememberMe?: boolean
}

interface FeishuSession {
  openId: string
  name: string
  avatarUrl: string
}

function mapFeishuToUser(session: FeishuSession): User {
  return {
    id: session.openId,
    username: session.name,
    email: '',
    avatar: session.avatarUrl,
  } as User
}

export const useUserStore = defineStore('user', () => {
  // 永远为空字符串：satisfies useApi 里 if (token) 的分支判断
  const storageToken = ref('')

  const user = ref<User | undefined>(undefined)
  const isAuthenticated = computed(() => !!user.value)

  async function me(): Promise<void> {
    const res = await fetch('/api/auth/me', { credentials: 'same-origin' })
    if (!res.ok) {
      user.value = undefined
      throw new Error(`auth/me failed: ${res.status}`)
    }
    const session = (await res.json()) as FeishuSession
    user.value = mapFeishuToUser(session)
  }

  async function logout(): Promise<void> {
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' })
    } catch (e) {
      console.error('[user.logout]', e)
    }
    user.value = undefined
    window.location.href = '/api/auth/login'
  }

  async function login(_credentials: LoginCredentials): Promise<void> {
    // 独立站点不使用账号密码登录；统一走飞书 OAuth。
    window.location.href = '/api/auth/login'
  }

  const isAuthenticatedInRegion = (_regionKey: string) => !!user.value
  const clearCurrentRegionData = () => {
    user.value = undefined
  }

  return {
    isAuthenticated,
    storageToken,
    user,

    login,
    me,
    logout,
    clearCurrentRegionData,
    isAuthenticatedInRegion,
  }
})
